import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { toast } from '@ohif/ui-next';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { DicomMetadataStore } from '@ohif/core';
/**
 * Componente para descargar los archivos de una serie
 */
interface DownloadButtonProps {
  seriesNumberCont: number;
  studyInstance: string;
}
const DownloadStudySeries: React.FC<DownloadButtonProps> = ({
  seriesNumberCont,
  studyInstance,
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const studyMetadata = DicomMetadataStore.getStudy(String(studyInstance));

  if (!studyMetadata?.series?.length) {
    return null;
  }

  const MAX_CONCURRENCY = 30;
  const MIN_CONCURRENCY = 10;
  let currentConcurrency = 50;

  const failedUrls = new Set<string>();

  const downloadImageWithRetry = async (
    url: string,
    fileName: string,
    zipFolder: JSZip,
    updateProgress: (p: number) => void,
    total: number,
    counter: { loaded: number }
  ): Promise<void> => {
    let attempts = 0;
    const maxRetries = 3;
    const startTime = Date.now();

    while (attempts <= maxRetries) {
      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/dicom' },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();
        zipFolder.file(fileName, blob);

        counter.loaded++;
        updateProgress(Math.round((counter.loaded / total) * 100));

        const timeTaken = Date.now() - startTime;
        if (timeTaken < 400 && currentConcurrency < MAX_CONCURRENCY) {
          currentConcurrency++;
        } else if (timeTaken > 2000 && currentConcurrency > MIN_CONCURRENCY) {
          currentConcurrency--;
        }

        return;
      } catch (err) {
        attempts++;
        if (attempts > maxRetries) {
          console.warn(`Falló definitivamente: ${fileName}`);
          failedUrls.add(url);
          return;
        }
        const delay = 100 * Math.pow(2, attempts); // 100ms, 200ms, 400ms...
        await new Promise(res => setTimeout(res, delay));
      }
    }
  };

  const generarZIP = async (
    images: { url: string; fileName: string; seriesUID: string }[]
  ): Promise<Blob> => {
    const zip = new JSZip();
    const queue = [...images];
    const counter = { loaded: 0 };

    const runWorker = async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item || failedUrls.has(item.url)) {
          continue;
        }

        const folder = zip.folder(item.seriesUID) || zip;
        await downloadImageWithRetry(
          item.url,
          item.fileName,
          folder,
          setProgress,
          images.length,
          counter
        );

        // Espera pequeña para liberar el hilo
        await new Promise(res => setTimeout(res, 0));
      }
    };

    // Inicialmente ejecuta con currentConcurrency
    const workers = Array.from({ length: currentConcurrency }, runWorker);
    await Promise.all(workers);

    return zip.generateAsync({ type: 'blob' });
  };
  const descarga = async () => {
    try {
      setLoading(true);
      setProgress(0);
      failedUrls.clear();

      if (!studyMetadata) {
        toast.error('No se encontró el estudio');
        return;
      }

      const studyUID = studyMetadata.StudyInstanceUID;
      const imageUrls: { url: string; fileName: string; seriesUID: string }[] = [];

      // Filtrar las series con el número de serie especificado
      const filteredSeries = studyMetadata.series.filter(
        series => series.SeriesNumber === seriesNumberCont
      );

      // Si no se encuentra la serie con el número de serie dado
      if (filteredSeries.length === 0) {
        toast.error('No se encontró la serie con el número especificado');
        return;
      }

      // Iterar solo sobre las series filtradas
      for (const series of filteredSeries) {
        const seriesUID = series.SeriesInstanceUID;
        for (const instance of series.instances) {
          const sopUID = instance.SOPInstanceUID;
          const rootUrl = instance.wadoRoot;
          const match = rootUrl.match(/^(https?:\/\/[^\/]+)/);

          if (match && studyUID && seriesUID && sopUID) {
            const wadoUrl =
              `${match[0]}/dcm4chee-arc/aets/MEDVIEWER/wado` +
              `?requestType=WADO&studyUID=${studyUID}&seriesUID=${seriesUID}&objectUID=${sopUID}&contentType=application/dicom`;

            const fileName = `DICOM_${seriesUID}_${instance.SOPInstanceUID}.dcm`;
            imageUrls.push({ url: wadoUrl, fileName, seriesUID });
          }
        }
      }

      if (imageUrls.length === 0) {
        toast.error('No se encontraron imágenes para descargar');
        return;
      }

      const estimatedSizeMB = (imageUrls.length * 5) / 1024;
      const estimatedTimeMin = Math.ceil(estimatedSizeMB / 1.5 / 60);

      toast.info(
        `Descargando ${imageUrls.length} imágenes (${estimatedSizeMB.toFixed(1)} MB estimados). Tiempo aprox: ${estimatedTimeMin} min.`,
        { duration: 6000 }
      );

      // Generar el archivo ZIP con todas las imágenes
      const zipBlob = await generarZIP(imageUrls);

      // Obtener el primer paciente de las imágenes descargadas para crear el nombre del archivo
      const firstInstance = filteredSeries[0].instances[0];
      const patientName = firstInstance?.PatientName?.[0]?.Alphabetic || 'Paciente';
      const protocol = firstInstance?.ProtocolName || '';
      const modality = firstInstance?.Modality || '';

      const filename = `${patientName}-${protocol}-${modality}-Serie-${seriesNumberCont}.zip`;

      // Guardar el archivo ZIP
      saveAs(zipBlob, filename);

      toast.success('Descarga completada');
    } catch (err) {
      console.error('Error al descargar estudio:', err);
      toast.error('Error durante la descarga');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      className={classnames(
        'bg-primary-active mx-auto mt-2 flex w-full items-center justify-center gap-[2px] p-[4px]',
        'hover:bg-primary-light/30 rounded text-gray-700'
      )}
      onClick={descarga}
      disabled={loading}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        width="16"
        height="16"
        className="bi bi-box-arrow-down"
        viewBox="0 0 16 16"
      >
        <path d="M2.95.4a1 1 0 0 1 .8-.4h8.5a1 1 0 0 1 .8.4l2.85 3.8a.5.5 0 0 1 .1.3V15a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V4.5a.5.5 0 0 1 .1-.3zM7.5 1H3.75L1.5 4h6zm1 0v3h6l-2.25-3zM15 5H1v10h14z" />
        <path
          fillRule="evenodd"
          d="M7.646 13.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 12.293V7.5a.5.5 0 0 0-1 0v4.793l-2.146-2.147a.5.5 0 0 0-.708.708z"
        />
      </svg>

      {loading && (
        <div
          className="spinner-container"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <span
            style={{ marginLeft: '10px' }}
            className="text-sm"
          >
            {Math.round(progress)}%
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            className="bi bi-arrow-clockwise animate-spin text-sm"
            viewBox="0 0 16 16"
          >
            <path
              fillRule="evenodd"
              d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2z"
            />
            <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466" />
          </svg>
        </div>
      )}
      {loading ? '    ' : 'Descargar '}
    </button>
  );
};

export { DownloadStudySeries };
